import React from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { POSTAPI, errStatusHandler } from "../../api-setup/api-helper";
import {
  GET_CONTRACT_ELEMENT_REPORT,
  GET_COMP_PLAN_SUMMARY_FILTER_COMP_PLAN,
  GET_COMP_PLAN_SUMMARY_FILTER_POSITION,
  GET_COMP_PLAN_SUMMARY_PROVIDER_PROFILE,
  GET_COMP_PLAN_SUMMARY_CONTRACT_DETAILS,
  GET_CATEGORY_FIELD,
} from "../../api-setup/api-endpoints";
import { getUserCredential } from "../../lib/cookies";
import { setNewMessage } from "../../redux/actions/setting-actions";
import {
  MOCK_CONTRACT_ELEMENT_REPORT,
  MOCK_FMG_MAYER_REPORT,
  MOCK_FTE_PAYNE_REPORT,
  MOCK_COMP_PLAN_FILTER,
  MOCK_POSITION_FILTER,
  MOCK_PROVIDER_PROFILE_FIELDS,
  MOCK_PROVIDER_PROFILE,
  MOCK_POSITION_CONTRACT_DETAILS,
} from "./mockData";

/**
 * The tenant field configuration orders Utilization Total last, away from the other two
 * utilization values. The report keeps the three adjacent so they read as a group. No-ops
 * when either field is absent from the configuration.
 */
function groupUtilizationFields(fields) {
  const totalIndex = fields.findIndex(
    (field) => field.filter_column_name === "utilization_total"
  );
  const clinicalIndex = fields.findIndex(
    (field) => field.filter_column_name === "utilization_clinical"
  );
  if (totalIndex === -1 || clinicalIndex === -1 || totalIndex === clinicalIndex + 1) {
    return fields;
  }

  const reordered = fields.slice();
  const [total] = reordered.splice(totalIndex, 1);
  const insertAt =
    reordered.findIndex(
      (field) => field.filter_column_name === "utilization_clinical"
    ) + 1;
  reordered.splice(insertAt, 0, total);
  return reordered;
}

/**
 * Comp attribute field metadata is configured per tenant, so the report renders whatever
 * SP_SELECT_CATEGORY_FIELD returns for 'comp_attr' — the same source the Provider Profile
 * screen uses. Column names may arrive table-qualified ("b.utilization_admin") while the
 * profile stored proc returns them flat, so the prefix is stripped here.
 */
function normalizeProviderFields(rows) {
  const fields = (rows || [])
    .filter((row) => row?.filter_label)
    .slice()
    .sort((a, b) => a.cat_fdm_order - b.cat_fdm_order)
    .map((row) => ({
      ...row,
      filter_column_name: row.filter_column_name?.includes(".")
        ? row.filter_column_name.split(".")[1]
        : row.filter_column_name,
    }));

  return groupUtilizationFields(fields);
}

function pickMockContractElementReport(compPlanKey, positionKey) {
  if (compPlanKey === 2 && positionKey === 2270) {
    return MOCK_CONTRACT_ELEMENT_REPORT;
  }
  if (compPlanKey === 2 && positionKey === 20724) {
    return MOCK_FMG_MAYER_REPORT;
  }
  if (compPlanKey === 26 && positionKey === 20927) {
    return MOCK_FTE_PAYNE_REPORT;
  }
  return {
    out_parameters: {
      out_total_rows: 0,
      out_ret_code: 0,
      out_error_msg: "",
    },
    row_result: [],
  };
}

function isContractElementMockEnabled() {
  const v = process.env.REACT_APP_CONTRACT_ELEMENT_USE_MOCK;
  if (v == null || v === "") return false;
  return String(v).trim().toLowerCase() === "true";
}

/** POSTAPI may return axios response (res.data) or unwrapped body when axios interceptors are active. */
function normalizeApiResponse(res) {
  if (!res) {
    return { body: null, payload: null };
  }

  const body =
    res?.data?.status !== undefined
      ? res.data
      : res?.status !== undefined
        ? res
        : null;

  if (!body) {
    return { body: null, payload: null };
  }

  const payload =
    body?.data?.out_parameters !== undefined ? body.data : body;

  return { body, payload };
}

export default function useContractElementSummary() {
  const dispatch = useDispatch();
  const history = useHistory();
  const cookiesData = getUserCredential();
  const header = {
    "x-access-token": cookiesData?.secretToken || "",
  };

  const [reportData, setReportData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [noData, setNoData] = React.useState(false);
  const [compPlanName, setCompPlanName] = React.useState("");
  const [positionName, setPositionName] = React.useState("");
  const [compPlanOptions, setCompPlanOptions] = React.useState([]);
  const [positionOptions, setPositionOptions] = React.useState([]);
  const [compPlanLoading, setCompPlanLoading] = React.useState(false);
  const [positionLoading, setPositionLoading] = React.useState(false);
  const [providerFields, setProviderFields] = React.useState([]);
  const [providerValues, setProviderValues] = React.useState(null);
  const [providerLoading, setProviderLoading] = React.useState(false);
  const [contractRows, setContractRows] = React.useState(null);
  const [contractLoading, setContractLoading] = React.useState(false);

  const getContractElementReport = async (params) => {
    setLoading(true);
    setError(null);
    setNoData(false);

    if (isContractElementMockEnabled()) {
      const mock = pickMockContractElementReport(
        params.in_comp_plan_key,
        params.in_position_key
      );
      if (mock.out_parameters.out_ret_code !== 0) {
        setError(mock.out_parameters.out_error_msg || "Unknown error");
        setReportData([]);
      } else {
        setReportData(mock.row_result || []);
        if (mock.row_result && mock.row_result.length > 0) {
          setCompPlanName(mock.row_result[0].comp_plan_name);
          setPositionName(mock.row_result[0].position_name);
        } else {
          setNoData(true);
        }
      }
      setLoading(false);
      return;
    }

    try {
      const res = await POSTAPI(false, GET_CONTRACT_ELEMENT_REPORT, null, header, params);

      if (res) {
        errStatusHandler(res, history, dispatch);
      }

      const { body: apiBody, payload } = normalizeApiResponse(res);
      const outParams = payload?.out_parameters || {};
      const retCode = Number(outParams.out_ret_code);

      if (!apiBody?.status) {
        const errMsg =
          apiBody?.msg ||
          apiBody?.errors?.msg ||
          outParams.out_error_msg ||
          "Failed to fetch report data";
        setError(errMsg);
        setReportData([]);
        return;
      }

      if (retCode > 0) {
        const rawMsg = outParams.out_error_msg || "";
        // Stored proc returns a "...not found" message when the selected comp
        // plan / position simply has no report data; treat that as empty, not an error.
        if (/not found/i.test(rawMsg)) {
          setReportData([]);
          setNoData(true);
        } else {
          const errMsg = rawMsg || "Error fetching report";
          setError(errMsg);
          setReportData([]);
          dispatch(
            setNewMessage({
              message: errMsg,
              type: "Error",
              show: true,
            })
          );
        }
      } else {
        const rows = payload?.row_result || [];
        setReportData(rows);
        if (rows.length > 0) {
          setCompPlanName(rows[0].comp_plan_name);
          setPositionName(rows[0].position_name);
        } else {
          setNoData(true);
        }
      }
    } catch (err) {
      setError(err?.message || "Unexpected error");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterRows = async (endpoint, params) => {
    const res = await POSTAPI(false, endpoint, null, header, params);

    if (res) {
      errStatusHandler(res, history, dispatch);
    }

    const { body: apiBody, payload } = normalizeApiResponse(res);
    const outParams = payload?.out_parameters || {};
    const retCode = Number(outParams.out_ret_code);

    // A ">0" return code from these filter procs means either a genuine error
    // or a "...not found" (no matching rows) — both resolve to empty options.
    if (!apiBody?.status || retCode > 0) {
      return [];
    }

    return payload?.row_result || [];
  };

  const getCompPlanFilter = async () => {
    setCompPlanLoading(true);

    if (isContractElementMockEnabled()) {
      setCompPlanOptions(MOCK_COMP_PLAN_FILTER);
      setCompPlanLoading(false);
      return MOCK_COMP_PLAN_FILTER;
    }

    try {
      // in_user_key / in_tenant_id are derived from the auth token on the server.
      const rows = await fetchFilterRows(GET_COMP_PLAN_SUMMARY_FILTER_COMP_PLAN, {});
      setCompPlanOptions(rows);
      return rows;
    } catch (err) {
      setCompPlanOptions([]);
      return [];
    } finally {
      setCompPlanLoading(false);
    }
  };

  const getPositionFilter = async (in_comp_plan_key) => {
    if (!in_comp_plan_key) {
      setPositionOptions([]);
      return [];
    }
    setPositionLoading(true);

    if (isContractElementMockEnabled()) {
      const rows = MOCK_POSITION_FILTER[in_comp_plan_key] || [];
      setPositionOptions(rows);
      setPositionLoading(false);
      return rows;
    }

    try {
      // in_user_key / in_tenant_id are derived from the auth token; user_filter
      // and user_role_abbr are kept empty on the server for now.
      const rows = await fetchFilterRows(GET_COMP_PLAN_SUMMARY_FILTER_POSITION, {
        in_comp_plan_key,
      });
      setPositionOptions(rows);
      return rows;
    } catch (err) {
      setPositionOptions([]);
      return [];
    } finally {
      setPositionLoading(false);
    }
  };

  const getProviderProfileFields = async () => {
    if (isContractElementMockEnabled()) {
      const rows = normalizeProviderFields(MOCK_PROVIDER_PROFILE_FIELDS);
      setProviderFields(rows);
      return rows;
    }

    try {
      const rows = await fetchFilterRows(GET_CATEGORY_FIELD, {
        in_filter: `AND a.category_code = 'comp_attr'`,
        in_module_name: "provider_position",
        in_sort_number: 1,
        in_sort_order: "ASC",
        in_tenant_id: cookiesData?.out_tenant_id,
      });
      const fields = normalizeProviderFields(rows);
      setProviderFields(fields);
      return fields;
    } catch (err) {
      setProviderFields([]);
      return [];
    }
  };

  /**
   * null means "not fetched yet" and keeps the section hidden; an empty object means
   * "fetched, but no profile data" and renders the configured labels with placeholders
   * so the report layout stays consistent. A failed lookup is deliberately not surfaced
   * as an error, since the section is supplementary to the KPI results.
   */
  const getProviderProfile = async (in_position_key) => {
    if (!in_position_key) {
      setProviderValues(null);
      return null;
    }
    setProviderLoading(true);

    if (isContractElementMockEnabled()) {
      const values = MOCK_PROVIDER_PROFILE[in_position_key] || {};
      setProviderValues(values);
      setProviderLoading(false);
      return values;
    }

    try {
      const res = await POSTAPI(
        false,
        GET_COMP_PLAN_SUMMARY_PROVIDER_PROFILE,
        null,
        header,
        { in_position_key }
      );

      if (res) {
        errStatusHandler(res, history, dispatch);
      }

      const { body: apiBody, payload } = normalizeApiResponse(res);
      const outParams = payload?.out_parameters || {};

      if (!apiBody?.status || Number(outParams.out_ret_code) > 0) {
        setProviderValues({});
        return null;
      }

      const values = payload?.row_result?.[0] || {};
      setProviderValues(values);
      return values;
    } catch (err) {
      setProviderValues({});
      return null;
    } finally {
      setProviderLoading(false);
    }
  };

  /**
   * null means "not fetched yet" and keeps the Contractual Details section hidden; an
   * empty array means "fetched, but the position has no contracts" and renders the
   * section's empty state. A failed lookup is deliberately not surfaced as a report
   * error, since the section is supplementary to the KPI results.
   */
  const getPositionContractDetails = async (in_position_key) => {
    if (!in_position_key) {
      setContractRows(null);
      return null;
    }
    setContractLoading(true);

    if (isContractElementMockEnabled()) {
      const mock = MOCK_POSITION_CONTRACT_DETAILS[in_position_key];
      const rows = mock?.row_result || [];
      setContractRows(rows);
      setContractLoading(false);
      return rows;
    }

    try {
      const res = await POSTAPI(
        false,
        GET_COMP_PLAN_SUMMARY_CONTRACT_DETAILS,
        null,
        header,
        { in_position_key }
      );

      if (res) {
        errStatusHandler(res, history, dispatch);
      }

      const { body: apiBody, payload } = normalizeApiResponse(res);
      const outParams = payload?.out_parameters || {};

      if (!apiBody?.status || Number(outParams.out_ret_code) > 0) {
        setContractRows([]);
        return [];
      }

      const rows = payload?.row_result || [];
      setContractRows(rows);
      return rows;
    } catch (err) {
      setContractRows([]);
      return [];
    } finally {
      setContractLoading(false);
    }
  };

  return [
    {
      reportData,
      loading,
      error,
      noData,
      compPlanName,
      positionName,
      compPlanOptions,
      positionOptions,
      compPlanLoading,
      positionLoading,
      providerFields,
      providerValues,
      providerLoading,
      contractRows,
      contractLoading,
    },
    {
      getContractElementReport,
      getCompPlanFilter,
      getPositionFilter,
      getProviderProfileFields,
      getProviderProfile,
      getPositionContractDetails,
    },
  ];
}
